import {put, call, all} from 'redux-saga/effects';
import Document from "../../entities/document";
import * as Actions from "../actions/documentActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";
import {ensureOperationId} from '../../lib/pendingRows';
import {tabSession} from '../../Auth/session';

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type DocumentAction = {
    documentId: string;
    document: Document;
    documents: Document[];
    sentAddedOn?: number;
    onQueued?: (saved: boolean) => void;
};

export function* fetchOneDocument(action: DocumentAction) {
	const session = tabSession.captureGeneration();
	try {
		const document: Document = yield call(api.findOne, 'document', action.documentId);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.DOCUMENT_FETCH_ONE_SUCCESS, documentId: action.documentId, document, sentAddedOn: action.sentAddedOn});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.DOCUMENT_FETCH_ONE_FAILURE, documentId: action.documentId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {DocumentAction} action
 */
export function* saveDocument(action: DocumentAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ DOCUMENT_SAVE saga', action.document?.id);

		if (action.document === undefined || action.document === null) {
			console.error('saveDocument received a null');
			throw new Error('saveDocument received a null');
		}

		if (!(action.document instanceof Document)) {
			action.document = new Document(action.document);
		}
		// OWA-1018: the reducer showed this save at dispatch, stamped after the version this tab holds
		// (OWA-1083), marked pending under this queue key
		const dispatchedAddedOn = action.document.addedOn;

		try {
			// Save to IndexedDB
			ensureOperationId(action.document);
			const addResult = yield call(idb.add, 'document', action.document);
			if (addResult.isErr()) {
				console.error(`⮂ saveDocument: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The save never reached the queue: its row returns to the latest accepted version
				yield put(Actions.revertDocumentSave(action.document, {sentAddedOn: dispatchedAddedOn}));
				yield put({type: Actions.DOCUMENT_SAVE_FAILURE, document: action.document, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copy replaces the row shown at dispatch
			// (the queue may have stamped it again)
			yield put({type: Actions.DOCUMENT_SAVE, document: action.document, dispatchedAddedOn, queuedAt: performance.now()});

			// A save never deletes autosaved drafts (OWA-958). The editor that wrote a draft
			// removes it when it saves; a draft typed somewhere else may be newer than this copy.
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue);
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: a save that never reached the queue returns its row to the latest accepted version
			if (!queueReported) yield put(Actions.revertDocumentSave(action.document, {sentAddedOn: dispatchedAddedOn}));
			reportQueued(false);
			yield put({type: Actions.DOCUMENT_SAVE_FAILURE, document: action.document, status: 500, err});

			console.error('Error saving to database', err);
		}
    } finally { reportQueued(false); }
}

/**
 * TODO: Update for saveMany
 * @param {DocumentAction} action
 * @param {Document[]} action.documents
 */
export function* saveManyDocuments(action: DocumentAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ DOCUMENT_SAVE_MANY saga', action.documents.map(document => document.id).join(','));

        if (!Array.isArray(action.documents)) {
            console.error('saveManyDocuments expected an array, received', action.documents);
            throw new Error('saveManyDocuments expected an array');
        }
		// OWA-1018: the reducer showed these saves at dispatch (see saveDocument), under these queue keys
		const dispatchedAddedOns: number[] = action.documents.map(document => document?.addedOn);
		const revertAll = () => action.documents.map((document, i) =>
			put(Actions.revertDocumentSave(document, {sentAddedOn: dispatchedAddedOns[i]})));

		try {
			const toSave: Document[] = [];

			// Save each item to IndexedDB
			for(let i = 0; i < action.documents.length; i++) {
				const document = action.documents[i];
				document.persisted = false;
				ensureOperationId(document);
				toSave.push(document);
			}

			const addResult = yield call(idb.add, 'document', toSave);
			if (addResult.isErr()) {
				console.error(`⮂ saveManyDocuments: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The saves never reached the queue: their rows return to the latest accepted versions
				yield all(revertAll());
				yield put({type: Actions.DOCUMENT_SAVE_MANY_FAILURE, documents: action.documents, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copies replace the rows shown at dispatch
			yield put({type: Actions.DOCUMENT_SAVE_MANY, documents: toSave, dispatchedAddedOns, queuedAt: performance.now()});
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue)
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: saves that never reached the queue return their rows to the latest accepted versions
			if (!queueReported) yield all(revertAll());
			reportQueued(false);
			yield put({type: Actions.DOCUMENT_SAVE_MANY_FAILURE, documents: action.documents, status: 500, err});

			console.error(`Error saving ${action.documents.length} documents to database`, err);
		}
    } finally { reportQueued(false); }
}

/**
 * @param {DocumentAction} action
 */
export function* removeDocument(action: DocumentAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
	if (!userId || action.document === undefined || action.document === null || !(action.document instanceof Document)) {
			console.error('removeDocument expected type Document, received', action.document);
			reportQueued(false);
			throw new Error('removeDocument expected type Document');
		}

	try {
		// Mark as deleted. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.document.status = 'D';
		action.document.addedOn = newHRTimestamp();
		action.document.addedBy = userId;

		// Save to IndexedDB
		ensureOperationId(action.document);
		const addResult = yield call(idb.add, 'document', action.document);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ removeDocument: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			yield put({type: Actions.DOCUMENT_REMOVE_FAILURE, document: action.document, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.DOCUMENT_REMOVE, document: action.document});
		session.assertCurrent();

        reportQueued(true);

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.DOCUMENT_REMOVE_FAILURE, document: action.document, status: 500, err});

		console.error('Error deleting from database', err);
	}
    finally { reportQueued(false); }
}

/**
	* Restore the last version of this Document.
	*
	* @param {DocumentAction} action
	*/
export function* restoreDocument(action: DocumentAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreDocument expected getCurrentUserId() to return a value');
        return;
    }

	if (action.document === undefined || action.document === null) {
        console.error('restoreDocument expected a Document, received null');
        throw new Error('restoreDocument expected a Document, received null');
    }
    else if (!(action.document instanceof Document)) action.document = new Document(action.document); 

	try {
		// Mark as restored. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.document.status = 'U';
		action.document.addedOn = newHRTimestamp();
		action.document.addedBy = userId;
		action.document.restore = true;

		// Save to IndexedDB
		ensureOperationId(action.document);
		const addResult = yield call(idb.add, 'document', action.document);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ restoreDocument: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.DOCUMENT_RESTORE_FAILURE, document: action.document, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.DOCUMENT_RESTORE, document: action.document});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.DOCUMENT_RESTORE_FAILURE, document: action.document, status: 500, err});

		console.error('Error restoring from database', err);
	}
}